<template>
    <div ref="rootRef" class="address-fields">
        <div class="input-row">
            <label class="field-label" :for="`${prefix}-country`">Country <span v-if="required" class="text-red-500">*</span></label>
            <PrimeSelect
                filter
                :inputId="`${prefix}-country`"
                :invalid="!!errors?.country"
                :modelValue="model.country"
                :options="countryOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Country"
                @update:modelValue="onCountryChange"
                @show="updateCountryPanelWidth"
                :panelStyle="{ width: countryPanelWidth ? `${countryPanelWidth}px` : 'auto' }"
                :pt="{ header: { style: { paddingTop: '4px' } }, label: selectA11y('country') }"
                class="w-full !rounded"
            >
                <template #value="slotProps">
                    <div v-if="slotProps.value" class="flex items-center">
                        <img
                            :alt="countryOptions.find((opt) => opt.value === slotProps.value)?.label"
                            :src="`/flags/${slotProps.value.toLowerCase()}.svg`"
                            style="width: 18px"
                            class="mr-2"
                            loading="lazy"
                        />
                        <div class="text-base">{{ countryOptions.find((opt) => opt.value === slotProps.value)?.label }}</div>
                    </div>
                    <span v-else>{{ slotProps.placeholder }}</span>
                </template>
                <template #option="slotProps">
                    <div class="flex items-center">
                        <img
                            v-if="slotProps.option.value"
                            :alt="slotProps.option.label"
                            :src="`/flags/${slotProps.option.value.toLowerCase()}.svg`"
                            style="width: 18px"
                            class="mr-2"
                        />
                        <div>{{ slotProps.option.label }}</div>
                    </div>
                </template>
            </PrimeSelect>
            <p v-if="errors?.country" :id="`${prefix}-country-error`" class="field-error">{{ errors.country }}</p>
        </div>

        <div class="input-row">
            <div class="field-label-row">
                <label class="field-label" :for="`${prefix}-line_1`">Address Line 1</label>
                <span class="optional-tag">Optional</span>
            </div>
            <PrimeInputText
                :id="`${prefix}-line_1`"
                v-bind="fieldA11y('line_1')"
                :modelValue="model.line_1"
                @update:modelValue="(v?: string) => emitField('line_1', sanitizeLine(v))"
                @keydown="addressLineKeydown"
                placeholder="Address Line 1"
                class="w-full"
            />
            <p v-if="errors?.line_1" :id="`${prefix}-line_1-error`" class="field-error">{{ errors.line_1 }}</p>
        </div>

        <div class="input-row">
            <div class="field-label-row">
                <label class="field-label" :for="`${prefix}-line_2`">Address Line 2</label>
                <span class="optional-tag">Optional</span>
            </div>
            <PrimeInputText
                :id="`${prefix}-line_2`"
                v-bind="fieldA11y('line_2')"
                :modelValue="model.line_2"
                @update:modelValue="(v?: string) => emitField('line_2', sanitizeLine(v))"
                @keydown="addressLineKeydown"
                placeholder="Address Line 2"
                class="w-full"
            />
            <p v-if="errors?.line_2" :id="`${prefix}-line_2-error`" class="field-error">{{ errors.line_2 }}</p>
        </div>

        <div class="input-trio">
            <div class="input-row">
                <label class="field-label" :for="`${prefix}-city`">City <span v-if="required" class="text-red-500">*</span></label>
                <PrimeInputText
                    :id="`${prefix}-city`"
                    v-bind="fieldA11y('city')"
                    :modelValue="model.city"
                    @update:modelValue="(v?: string) => emitField('city', sanitizeCityState(v))"
                    @keydown="cityStateKeydown"
                    placeholder="City"
                    class="w-full"
                />
                <p v-if="errors?.city" :id="`${prefix}-city-error`" class="field-error">{{ errors.city }}</p>
            </div>
            <div class="input-row">
                <label class="field-label" :for="`${prefix}-region`">State / Region <span v-if="required" class="text-red-500">*</span></label>
                <PrimeSelect
                    v-if="stateOptions.length > 0"
                    :inputId="`${prefix}-region`"
                    :invalid="!!errors?.region"
                    :modelValue="model.region"
                    @update:modelValue="(v?: string) => emitField('region', v ?? '')"
                    :options="stateOptions"
                    optionLabel="label"
                    optionValue="label"
                    placeholder="State / Region"
                    :pt="{ label: selectA11y('region') }"
                    class="w-full !rounded"
                />
                <PrimeInputText
                    v-else
                    :id="`${prefix}-region`"
                    v-bind="fieldA11y('region')"
                    :modelValue="model.region"
                    @update:modelValue="(v?: string) => emitField('region', sanitizeCityState(v))"
                    @keydown="cityStateKeydown"
                    placeholder="State / Region"
                    class="w-full"
                />
                <p v-if="errors?.region" :id="`${prefix}-region-error`" class="field-error">{{ errors.region }}</p>
            </div>
            <div class="input-row">
                <label class="field-label" :for="`${prefix}-postal_code`">Postal Code <span v-if="required" class="text-red-500">*</span></label>
                <PrimeInputText
                    :id="`${prefix}-postal_code`"
                    v-bind="fieldA11y('postal_code')"
                    :modelValue="model.postal_code"
                    @update:modelValue="onPostalChange"
                    @keydown="postalKeydown"
                    @wheel="disablePostalScroll"
                    :maxlength="model.country === 'US' ? US_POSTAL_CODE_LENGTH : POSTAL_CODE_MAX"
                    placeholder="Postal Code"
                    class="w-full"
                />
                <p v-if="errors?.postal_code" :id="`${prefix}-postal_code-error`" class="field-error">{{ errors.postal_code }}</p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import countriesData from '@/assets/json/countries.json';

export interface AddressValue {
    line_1: string;
    line_2: string;
    city: string;
    region: string;
    postal_code: string;
    country: string;
}

const props = defineProps<{
    modelValue: Partial<AddressValue>;
    required?: boolean;
    /** Prefix for the field ids the labels point at; generated when the host has no name for them. */
    idPrefix?: string;
    /** A message per field, from the host form (a server rejection, say), read as that field's error. */
    errors?: Partial<Record<keyof AddressValue, string>>;
}>();

const generatedPrefix = useId();
const prefix = computed(() => props.idPrefix ?? generatedPrefix);

/**
 * Which fields this form will refuse to submit without, when the caller says the address is
 * required at all. Line 1 and line 2 are not in it — a mailing address with no street line
 * is a real one in parts of the world this form serves, and the callers' own validation
 * agrees.
 *
 * Announced with `aria-required`, alongside the asterisk beside each label: the asterisk is
 * the sighted half of the same statement and neither one covers for the other.
 */
const REQUIRED_KEYS: Array<keyof AddressValue> = ['country', 'city', 'region', 'postal_code'];

const requiredAttrs = (key: keyof AddressValue) =>
    props.required && REQUIRED_KEYS.includes(key) ? { 'aria-required': true } : {};

/** A text field's `aria-required`, plus its invalid/describedby pair while it carries a message. */
function fieldA11y(key: keyof AddressValue) {
    const message = props.errors?.[key];
    return {
        ...requiredAttrs(key),
        ...(message ? { 'aria-invalid': true, 'aria-describedby': `${prefix.value}-${key}-error` } : {}),
    };
}

// The selects' focusable combobox is PrimeVue's `label` element, so the error
// id is passed through `pt` there; `invalid` already marks the control.
function selectA11y(key: keyof AddressValue) {
    const message = props.errors?.[key];
    return {
        ...requiredAttrs(key),
        ...(message ? { 'aria-describedby': `${prefix.value}-${key}-error` } : {}),
    };
}

const emit = defineEmits<{
    (e: 'update:modelValue', value: AddressValue): void;
}>();

// Mirror AddAddressModal's country list — same flags, same data source.
const countryOptions = countriesData.map((c) => ({ label: c.name, value: c.code }));

const stateOptions = ref<{ label: string; value: string }[]>([]);
const rootRef = ref<HTMLElement | null>(null);
const countryPanelWidth = ref(0);

const model = computed<AddressValue>(() => ({
    line_1: props.modelValue.line_1 ?? '',
    line_2: props.modelValue.line_2 ?? '',
    city: props.modelValue.city ?? '',
    region: props.modelValue.region ?? '',
    postal_code: props.modelValue.postal_code ?? '',
    country: props.modelValue.country ?? '',
}));

function emitField<K extends keyof AddressValue>(key: K, value: AddressValue[K]) {
    emit('update:modelValue', { ...model.value, [key]: value });
}

function updateCountryPanelWidth() {
    if (rootRef.value) {
        countryPanelWidth.value = rootRef.value.offsetWidth;
    }
}

const personStore = usePersonStore();

async function fetchStates(countryCode: string) {
    try {
        const data = await personStore.fetchStatesByCode(countryCode);
        // Discard a response superseded by a newer country selection — otherwise
        // a slower response for the previous country can overwrite the regions
        // for the country now selected, letting a mismatched region/country
        // combination be submitted.
        if (model.value.country !== countryCode) return;
        stateOptions.value = data?.areas?.map((area: any) => ({
            label: area.name,
            value: area.code,
        })) ?? [];
    } catch {
        if (model.value.country !== countryCode) return;
        stateOptions.value = [];
    }
}

async function onCountryChange(code: string) {
    emit('update:modelValue', { ...model.value, country: code, region: '', postal_code: '' });
    // Clear immediately rather than waiting for the fetch — otherwise the
    // previous country's regions stay selectable in the dropdown for the
    // entire loading window.
    stateOptions.value = [];
    await fetchStates(code);
}

watch(
    () => model.value.country,
    (code) => {
        if (code && stateOptions.value.length === 0) {
            fetchStates(code);
        }
    },
    { immediate: true }
);

// Same input/keydown sanitizers as AddAddressModal: Unicode letters (`\p{L}`,
// not ASCII-only — international addresses like "Montréal" must survive),
// digits, space, and `- , . ' / #` in address lines; letters / spaces /
// hyphens for city/state; country-aware postal.
function sanitizeLine(value?: string): string {
    return (value ?? '').replace(/[^\p{L}0-9\s\-,.'/#]/gu, '');
}

function sanitizeCityState(value?: string): string {
    return (value ?? '').replace(/[^\p{L}\s\-]/gu, '');
}

function addressLineKeydown(e: KeyboardEvent) {
    const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if ((e.ctrlKey || e.metaKey) && /^[acvxACVX]$/.test(e.key)) return;
    // Dead keys start diacritic composition (macOS/Linux) — blocking them
    // would make accented letters untypable.
    if (e.key === 'Dead') return;
    if (!/^[\p{L}0-9\s\-,.'/#]$/u.test(e.key) && !allowed.includes(e.key)) e.preventDefault();
}

function cityStateKeydown(e: KeyboardEvent) {
    const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if ((e.ctrlKey || e.metaKey) && /^[acvxACVX]$/.test(e.key)) return;
    if (e.key === 'Dead') return;
    if (!/^[\p{L}\s\-]$/u.test(e.key) && !allowed.includes(e.key)) e.preventDefault();
}

function postalKeydown(e: KeyboardEvent) {
    const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if ((e.ctrlKey || e.metaKey) && /^[acvxACVX]$/.test(e.key)) return;
    if (model.value.country === 'US') {
        if (!/^\d$/.test(e.key) && !allowed.includes(e.key)) e.preventDefault();
    } else {
        if (!/^[a-zA-Z0-9\-]$/.test(e.key) && !allowed.includes(e.key)) e.preventDefault();
    }
}

/** A US ZIP code is five digits. */
const US_POSTAL_CODE_LENGTH = 5;

/** As wide as `person_addresses.address_postal_code` (varchar(16)); app-api refuses a longer code. */
const POSTAL_CODE_MAX = 16;

function onPostalChange(value?: string) {
    const raw = value ?? '';
    if (model.value.country === 'US') {
        emitField('postal_code', raw.replace(/\D/g, '').slice(0, US_POSTAL_CODE_LENGTH));
    } else {
        emitField('postal_code', raw.replace(/[^a-zA-Z0-9\-]/g, '').slice(0, POSTAL_CODE_MAX));
    }
}

function disablePostalScroll(e: WheelEvent) {
    if (model.value.country === 'US') {
        (e.target as HTMLElement).blur();
        e.preventDefault();
    }
}
</script>

<style scoped lang="scss">
.address-fields {
    @apply flex flex-col gap-3;
}

.input-row {
    @apply flex flex-col gap-1.5 w-full;
}

.input-trio {
    @apply grid grid-cols-1 sm:grid-cols-3 gap-3;
}

.field-label {
    @apply text-sm font-medium text-zinc-700;
}

.field-label-row {
    @apply flex items-center justify-between;

    .optional-tag {
        @apply text-xs text-zinc-400;
    }
}

.field-error {
    @apply text-sm text-red-600 m-0;
}
</style>
